<template>
  <v-container fluid>
    <v-row>
      <global-EventFilterMenu header="Event Filter" showStallholders />
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1 d-flex align-center">
            <div class="d-flex align-center">
              <v-icon left dense>mdi-cellphone-link</v-icon>
              Devices
            </div>
            <v-spacer />
            <v-btn
              small
              text
              class="text-none"
              @click="showUnlinked = !showUnlinked"
            >
              <v-icon left small>
                {{ showUnlinked ? "mdi-eye-off" : "mdi-eye" }}
              </v-icon>
              Toggle Show Unlinked Devices
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="headers"
              :items="filteredDevices"
              :loading="devicesHealthLoading"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              class="elevation-5"
              mobile-breakpoint="0"
            >
              <template v-slot:top>
                <!-- Empty top slot to remove toolbar gap -->
              </template>

              <template v-slot:[`item.Status`]="{ item }">
                <span :class="statusClass(item)">{{ item.Status || "—" }}</span>
              </template>

              <template v-slot:[`item.BatteryPercentage`]="{ item }">
                <span :class="batteryClass(item)">
                  {{ formatBattery(item.BatteryPercentage) }}
                </span>
              </template>

              <template v-slot:[`item.ChargingState`]="{ item }">
                {{ formatCharging(item.ChargingState) }}
              </template>

              <template v-slot:[`item.IPAddress`]="{ item }">
                {{ wifiOnly(item, item.IPAddress) }}
              </template>

              <template v-slot:[`item.WiFiSSID`]="{ item }">
                {{ wifiOnly(item, item.WiFiSSID) }}
              </template>

              <template v-slot:[`item.WiFiRSSI`]="{ item }">
                <span :class="rssiClass(item)">
                  {{ wifiOnly(item, item.WiFiRSSI) }}
                </span>
              </template>

              <template v-slot:[`item.Timestamp`]="{ item }">
                <span :class="lastContactClass(item)">
                  {{ formatLastContact(item.Timestamp) }}
                </span>
              </template>

              <template v-slot:[`item.StallholderName`]="{ item }">
                {{ item.StallholderName || "—" }}
              </template>

              <template v-slot:[`item.StallName`]="{ item }">
                {{ item.StallName || "—" }}
              </template>

              <template v-slot:[`item.TerminalNumber`]="{ item }">
                {{
                  item.TerminalNumber === null ||
                  item.TerminalNumber === undefined
                    ? "—"
                    : item.TerminalNumber
                }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";

export default {
  name: "Devices",
  data: () => ({
    showUnlinked: false,
    sortBy: "TerminalNumber",
    sortDesc: false,
    headers: [
      { text: "Device ID", value: "DeviceId" },
      { text: "Status", value: "Status" },
      { text: "Stallholder", value: "StallholderName" },
      { text: "Stall", value: "StallName" },
      {
        text: "Terminal",
        value: "TerminalNumber",
        sort: (a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0),
      },
      { text: "Battery", value: "BatteryPercentage" },
      { text: "Charging", value: "ChargingState" },
      { text: "Connection", value: "ConnectionMethod" },
      { text: "WiFi MAC", value: "WiFiMAC" },
      { text: "WiFi IP", value: "IPAddress" },
      { text: "SSID", value: "WiFiSSID" },
      { text: "RSSI", value: "WiFiRSSI" },
      { text: "Time Zone", value: "TimeZone" },
      { text: "Last Contact", value: "Timestamp" },
    ],
  }),
  computed: {
    ...mapState("dashboard", ["devicesHealth", "devicesHealthLoading"]),
    ...mapState("filter", ["searchStallholders", "searchStalls"]),
    filteredDevices() {
      const rows = Array.isArray(this.devicesHealth) ? this.devicesHealth : [];
      const shFilter = this.normalizeIdFilter(this.searchStallholders);
      const stallFilter = this.normalizeIdFilter(this.searchStalls);

      return rows.filter((row) => {
        if (row.Unlinked) {
          return this.showUnlinked;
        }

        if (shFilter.length && !shFilter.includes(row.StallholderId)) {
          return false;
        }
        if (stallFilter.length && !stallFilter.includes(row.StallId)) {
          return false;
        }
        return true;
      });
    },
  },
  methods: {
    normalizeIdFilter(value) {
      if (!value || value === "notSet" || value === "all") {
        return [];
      }
      if (Array.isArray(value)) {
        return value
          .map((item) =>
            typeof item === "object"
              ? item.StallholderId || item.StallId || item
              : item
          )
          .filter(Boolean);
      }
      return [];
    },
    isWifi(item) {
      return String(item.ConnectionMethod || "").toLowerCase() === "wifi";
    },
    wifiOnly(item, value) {
      if (!this.isWifi(item)) {
        return "";
      }
      return value === null || value === undefined || value === ""
        ? "—"
        : value;
    },
    formatBattery(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      const text = String(value);
      return text.endsWith("%") ? text : `${text}%`;
    },
    formatCharging(value) {
      if (value === true || value === "true") {
        return "Yes";
      }
      if (value === false || value === "false") {
        return "No";
      }
      return value === null || value === undefined || value === ""
        ? "—"
        : value;
    },
    formatLastContact(timestamp) {
      if (!timestamp) {
        return "—";
      }
      const parsed = moment(
        String(timestamp),
        ["YYYYMMDDHHmmss", "YYYYMMDDHHmm", moment.ISO_8601],
        true
      );
      if (!parsed.isValid()) {
        return String(timestamp);
      }
      return parsed.format("DD/MM/YYYY HH:mm:ss");
    },
    batteryValue(item) {
      const raw = item.BatteryPercentage;
      if (raw === null || raw === undefined || raw === "") {
        return null;
      }
      return parseInt(String(raw).replace("%", ""), 10);
    },
    rssiValue(item) {
      if (!this.isWifi(item)) {
        return null;
      }
      if (item.WiFiRSSI === null || item.WiFiRSSI === undefined || item.WiFiRSSI === "") {
        return null;
      }
      return Number(item.WiFiRSSI);
    },
    minutesSinceContact(item) {
      if (!item.Timestamp) {
        return null;
      }
      const parsed = moment(
        String(item.Timestamp),
        ["YYYYMMDDHHmmss", "YYYYMMDDHHmm", moment.ISO_8601],
        true
      );
      if (!parsed.isValid()) {
        return null;
      }
      return moment().diff(parsed, "minutes");
    },
    statusClass(item) {
      return item.Status && item.Status !== "Logged In"
        ? "device-cell-red"
        : "";
    },
    batteryClass(item) {
      const value = this.batteryValue(item);
      if (value === null || Number.isNaN(value)) {
        return "";
      }
      if (value <= 15) {
        return "device-cell-red";
      }
      if (value <= 25) {
        return "device-cell-orange";
      }
      return "";
    },
    rssiClass(item) {
      const value = this.rssiValue(item);
      if (value === null || Number.isNaN(value)) {
        return "";
      }
      if (value <= -76) {
        return "device-cell-red";
      }
      if (value <= -67) {
        return "device-cell-orange";
      }
      return "";
    },
    lastContactClass(item) {
      const minutes = this.minutesSinceContact(item);
      if (minutes === null) {
        return item.Status === "Offline" ? "device-cell-red" : "";
      }
      return minutes > 11 ? "device-cell-red" : "";
    },
  },
};
</script>

<style scoped>
.device-cell-red {
  color: #ff5252;
  font-weight: 600;
}
.device-cell-orange {
  color: #ff9800;
  font-weight: 600;
}
</style>
